/**
 * Presentation helpers for the Market Regime page. Pure, so wording and
 * colour stay consistent and can be unit tested without rendering.
 */

/** Text colour for an indicator or group signal. */
export const signalColor = (signal) => {
  if (signal === 'positive') return 'var(--success-color)';
  if (signal === 'negative') return 'var(--danger-color)';
  return 'var(--text-secondary)';
};

/** Badge class for a group verdict: green good, red bad, amber mixed or complacent. */
export const signalBadge = (signal) => {
  if (signal === 'positive') return 'badge-success';
  if (signal === 'negative') return 'badge-danger';
  return 'badge-warning';
};

/** CSS-variable pair for an alert block, keyed by the backend's severity. */
export const severityColors = (severity) => {
  switch (severity) {
    case 'alert':    return { bg: 'var(--severity-alert-bg)', border: 'var(--severity-alert-border)' };
    case 'warn':     return { bg: 'var(--severity-warn-bg)',  border: 'var(--severity-warn-border)' };
    case 'tailwind': return { bg: 'var(--severity-good-bg)',  border: 'var(--severity-good-border)' };
    default:         return { bg: 'var(--severity-info-bg)',  border: 'var(--severity-info-border)' };
  }
};

/** An indicator value at the precision its definition asks for. */
export const formatValue = (value, decimals = 4) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const d = Math.max(0, Math.min(6, decimals));
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};

/**
 * A change in the indicator's own terms: percent for prices and ratios,
 * percentage points for yields and spreads (changeMode "abs").
 */
export const formatChange = (change, mode = 'pct') => {
  if (change == null || Number.isNaN(Number(change))) return '—';
  const n = Number(change);
  const sign = n > 0 ? '+' : '';
  if (mode === 'abs') return `${sign}${n.toFixed(2)} pts`;
  return `${sign}${n.toFixed(1)}%`;
};

/** Colour for a change, by whether the move is good for the indicator's question. */
export const changeColor = (change, polarity) => {
  if (change == null || !polarity || Number(change) === 0) return 'var(--text-secondary)';
  return Number(change) * polarity > 0 ? 'var(--success-color)' : 'var(--danger-color)';
};

/** [{ date, value }] for one indicator out of the history rows, skipping days it had no value. */
export const seriesFor = (history, id) =>
  (history || [])
    .map((row) => ({ date: row.date, value: row.indicatorValues?.[id] }))
    .filter((p) => p.value != null)
    .map((p) => ({ date: p.date, value: Number(p.value) }));

/** Indicator definitions keyed by id, flattened out of the grouped definitions list. */
export const definitionsById = (definitions) => {
  const map = {};
  (definitions || []).forEach((g) => (g.indicators || []).forEach((i) => { map[i.id] = { ...i, group: g.id }; }));
  return map;
};

/** Horizontal guide lines worth drawing on an indicator's chart (Russ's thresholds). */
export const referenceLines = (id) => {
  if (id === 'VIX_VIX3M') return [{ y: 1, label: 'stress' }, { y: 0.8, label: 'complacent' }];
  if (id === 'US10Y_2Y') return [{ y: 0, label: 'inverted' }];
  return [];
};
